'use client';
import { useEffect, useRef } from 'react';

/**
 * Sección 02 · La red. Diagrama canvas de Demanda (izq) ↔ ARKU (centro) ↔ Oferta (der).
 * Nodos de cada lado disparan pulsos hacia el ring central de ARKU.
 */
function ConnectCanvas() {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    let raf = 0;
    let w = 0, h = 0;
    let mobile = false;
    type Side = 'l' | 'r';
    interface Node { x: number; y: number; side: Side }
    const nodes: Node[] = [];
    const pulses: Array<{ from: Node; t: number; toCenter: boolean; color: string }> = [];

    function resize() {
      const rect = canvas!.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      mobile = w < 760;
      canvas!.width = w * dpr;
      canvas!.height = h * dpr;
      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
      nodes.length = 0;
      const per = 14;
      const seed = (side: Side) => {
        for (let i = 0; i < per; i++) {
          const t = (i + 0.5) / per;
          if (mobile) {
            const yy = side === 'l' ? h * 0.18 : h * 0.82;
            nodes.push({ x: w * (0.12 + 0.76 * t), y: yy + (Math.random() - 0.5) * 22, side });
          } else {
            const xx = side === 'l' ? w * 0.18 : w * 0.82;
            nodes.push({ x: xx + (Math.random() - 0.5) * 36, y: h * (0.15 + 0.7 * t), side });
          }
        }
      };
      seed('l'); seed('r');
    }

    function center() {
      return { x: w / 2, y: h / 2 };
    }

    function step() {
      ctx!.clearRect(0, 0, w, h);
      const c = center();

      // soft connections side -> center
      for (const n of nodes) {
        const dx = c.x - n.x, dy = c.y - n.y;
        const d = Math.hypot(dx, dy);
        const alpha = Math.max(0, 0.08 - d / (Math.max(w, h) * 8));
        ctx!.strokeStyle = `rgba(122,160,204,${(0.05 + alpha).toFixed(3)})`;
        ctx!.lineWidth = 1;
        ctx!.beginPath();
        ctx!.moveTo(n.x, n.y);
        ctx!.lineTo(c.x, c.y);
        ctx!.stroke();
      }

      // pulses
      for (let i = pulses.length - 1; i >= 0; i--) {
        const p = pulses[i];
        p.t += reduce ? 0 : 0.018;
        if (p.t >= 1) { pulses.splice(i, 1); continue; }
        const x = p.from.x + (c.x - p.from.x) * p.t;
        const y = p.from.y + (c.y - p.from.y) * p.t;
        ctx!.beginPath();
        ctx!.arc(x, y, 2.4, 0, Math.PI * 2);
        ctx!.fillStyle = p.color;
        ctx!.shadowColor = p.color;
        ctx!.shadowBlur = 12;
        ctx!.fill();
        ctx!.shadowBlur = 0;
      }

      // nodes
      for (const n of nodes) {
        ctx!.beginPath();
        ctx!.arc(n.x, n.y, 2.6, 0, Math.PI * 2);
        ctx!.fillStyle = n.side === 'l' ? 'rgba(79,183,131,0.85)' : 'rgba(166,198,232,0.85)';
        ctx!.fill();
      }
      raf = requestAnimationFrame(step);
    }

    function spawn() {
      if (nodes.length === 0) return;
      const n = nodes[(Math.random() * nodes.length) | 0];
      pulses.push({
        from: n,
        t: 0,
        toCenter: true,
        color: n.side === 'l' ? 'rgba(79,183,131,0.95)' : 'rgba(166,198,232,0.95)',
      });
    }

    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    resize();
    step();
    const interval = reduce ? 0 : window.setInterval(spawn, 420);

    return () => {
      cancelAnimationFrame(raf);
      if (interval) clearInterval(interval);
      ro.disconnect();
    };
  }, []);

  return <canvas ref={ref} />;
}

export function Red() {
  return (
    <section className="section connect" id="red">
      <div className="wrap">
        <div className="sec-index">
          <span className="n">02</span>
          <span className="ln" />
          <span className="t">La red</span>
        </div>
        <h2 className="display">
          Una base. <em>Dos lados</em> del mercado.
        </h2>
        <p className="lede">
          El directorio que ve el comprador y las oportunidades que ve el proveedor salen de la
          misma base de conocimiento. Cada requerimiento que entra se enruta, vivo, a quien
          califica.
        </p>
        <div className="connect-stage">
          <ConnectCanvas />
          <div className="connect-label l">
            <span className="big">Demanda</span>
            <span className="cnt">6,500+</span>
          </div>
          <div className="connect-label r">
            <span className="big">Oferta</span>
            <span className="cnt">20,000+</span>
          </div>
          <div className="connect-core">
            <div className="ring">
              <svg viewBox="0 0 52 52" fill="none">
                <circle cx="26" cy="26" r="22" stroke="currentColor" strokeOpacity=".5" strokeWidth="1.5" />
                <path d="M26 6 A20 20 0 0 1 46 26" stroke="currentColor" strokeWidth="2.5" />
                <circle cx="26" cy="26" r="11" stroke="currentColor" strokeOpacity=".35" strokeWidth="1.2" />
                <circle cx="26" cy="26" r="3" fill="currentColor" />
              </svg>
            </div>
            <div className="cap">ARKU · capa de inteligencia</div>
          </div>
        </div>
      </div>
    </section>
  );
}
